import { DictType, ProjectFile } from "@pma/types";
import { useMutation, useQuery } from "@tanstack/react-query";
import { toast } from "@hooks/use-toast";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@components/common/tooltip";
import { useState } from "react";
import useTranslator from "@hooks/use-translator";
import {
  ADD_FILES_TO_NOTE_ENDPOINT,
  DIGITAL_OCEAN_FILES_ENDPOINT,
  GET_FILES_ENDPOINT,
  SERVER_URL,
} from "@pma/utils/api-routes";
import axios from "axios";
import { parseCookies } from "nookies";
import { ACCESS_TOKEN } from "@pma/utils/constants";
import { RenderFileIcon } from "../files/files-view";
import AddFileToNote from "../notifications/add-file-to-note";
import { FilePreview } from "../dashboard/preview-file";
import { cn } from "@pma/utils/helpers";
import {
  HoverCard,
  HoverCardContent,
  HoverCardTrigger,
} from "@components/common/hover-card";
import { Popover, PopoverContent, PopoverTrigger } from "../common/popover";
import DeleteFile from "../dashboard/delete-file";

interface Props {
  noteId: number;
  files: Array<DictType>;
  size?: "lg" | "sm";
  showAsFolder?:boolean
}
const AttachFileToNote = ({ noteId, files, size ,showAsFolder=false}: Props) => {
  const [file, setFile] = useState<[any] | []>([]);
  const [searchParam] = useSearchParams();
  const newParams = new URLSearchParams(searchParam); 

  const accessToken = parseCookies()[ACCESS_TOKEN];

  const navigate = useNavigate();
  const { translate } = useTranslator();

  const addToFileTask = async (files: ProjectFile[]) => {
    const file_ids = files?.map((f) => {
      return f.id;
    });
    const response = await axios.post(
      SERVER_URL + ADD_FILES_TO_NOTE_ENDPOINT,
      {
        note_id: noteId,
        inform_users: true,
        files_id: file_ids,
      },
      {
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
      },
    );

    return response.data;
  };

  const addFileToTaskMutation = useMutation({
    mutationKey: ["add-file-to-task", noteId],
    mutationFn: addToFileTask,
    onSuccess: (data) => {
      //Trigger Revalidate tasks
      newParams.set('revalidate', 'tasks');
      navigate(`?${newParams.toString()}`);
    },
    onError: (error: DictType) => {
      const errorMessage = error?.response?.data?.message;

      toast({
        title: translate("Error"),
        description: translate(errorMessage || "Error occurred"),
        variant: "error",
      });
    },
  });

  const uploadFile = async () => {
    const formData = new FormData();

    if (!file) {
      return;
    }

    if(file?.length > 0){
      [...file]?.forEach((val)=>{
        formData.append("files", val);

      })
    }


    const url = `${DIGITAL_OCEAN_FILES_ENDPOINT}?token=${accessToken}`;

    const response = await axios.post(url, formData, {
      headers: {
        accept: "*/*",
        "Content-Type": "multipart/form-data",
      },
    });

    return response?.data;
  };

  const uploadFileMutation = useMutation({
    mutationKey: ["upload-file"],
    mutationFn: uploadFile,
    onSuccess: (data) => {
      const payload = data as ProjectFile[];
      setFile([]);
      toast({
        title: translate("Success"),
        description: translate("File uploaded successfully"),
        variant: "success",
      });
      // Link digital ocean file to  task
      addFileToTaskMutation.mutate(payload);
    },
    onError: (error: DictType) => {
      const errorMessage = error?.response?.data?.message;

      toast({
        title: translate("Error"),
        description: errorMessage || translate("Unable to upload file at this time"),
        variant: "error",
      });
    },
  });

  return (
    <div
      className={cn(
        "flex items-start gap-1 p-1",
        size === "lg" ? "w-full items-center" : "w-[100px]",
      )}
    >
    {showAsFolder  ?<Popover>
        <PopoverTrigger className="z-10 relative">
          {files?.length ? (
            <>
              <small className="absolute text-[10px] -top-2.5 -right-1 h-3 w-3 rounded-full shadow-md bg-slate-300 text-primary flex items-center justify-center">
                {files?.length}
              </small>
              <RenderFileIcon type={"file"} size={size} />
            </>
          ) : (
            <></>
          )}
        </PopoverTrigger>
        <PopoverContent className="grid grid-cols-3 gap-2 flex-1 z-50">
          {files?.length > 0 ? (
            <>
              {files?.map((file: any, index: number) => (
                <div className="relative z-10" key={index}>
                  <div className="absolute -right-3 -top-2">
                    <DeleteFile file={file} />
                  </div>

                  <TaskFilesPreview fileId={file?.id} size={"lg"} />
                  <span className="truncate overflow-hidden max-w-[90px] text-ellipsis text-xs text-center">
                    {file?.name && (
                      <span>
                        {file?.name?.slice(0, 5)}...{file.type}
                      </span>
                    )}
                  </span>
                </div>
              ))}
            </>
          ) : (
            <div className="col-span-3">{translate("Folder is empty")}</div>
          )}
        </PopoverContent>
      </Popover>
      :<HoverCard>
        <HoverCardTrigger>
          {files?.length > 3 && size !== "lg" ? (
            <RenderFileIcon type={"file"} size={size} />
          ) : (
            <div className="grid grid-cols-3 flex-1">
              {files?.map((file: any, index: number) => (
                <TaskFilesPreview
                  fileId={file?.id}
                  key={index}
                  size={size || "sm"}
                />
              ))}
            </div>
          )}
        </HoverCardTrigger>
        {files?.length > 3 && size !== "lg" && (
          <HoverCardContent className="grid grid-cols-3 gap-2 flex-1">
            {files?.map((file: any, index: number) => (
              <TaskFilesPreview
                fileId={file?.id}
                key={index}
                size={size || "sm"}
              />
            ))}
          </HoverCardContent>
        )}
      </HoverCard>}
      <AddFileToNote
        context="task"
        setFile={setFile}
        reset={file && file.length < 0}
        uploadFunction={uploadFileMutation.mutate}
      />
    </div>
  );
};

export default AttachFileToNote;

// Component to visualize Files in task
const TaskFilesPreview = ({
  fileId,
  size,
}: {
  fileId: number;
  size: "sm" | "lg";
}) => {
  const accessToken = parseCookies()[ACCESS_TOKEN];

  const getFileById = async () => {
    const response = await axios.get(
      SERVER_URL + `${GET_FILES_ENDPOINT}${fileId}`,
      {
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
      },
    );

    return response.data;
  };

  const { data: fileContent } = useQuery({
    queryKey: ["file-content", fileId],
    queryFn: getFileById,
  });

  return (
    <FilePreview file={fileContent}>
      <Tooltip>
        <TooltipTrigger>
          <RenderFileIcon type={fileContent?.type} size={size} />
        </TooltipTrigger>
        <TooltipContent>
          <h4> {fileContent?.name}</h4>
        </TooltipContent>
      </Tooltip>
    </FilePreview>
  );
};
